/**
 * FilesPage - Floor Plan Files Management
 *
 * Displays uploaded floor plans organized by floor.
 * Allows viewing floor plan images and triggering re-analysis.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { Image, UploadSimple, ArrowsClockwise, Eye, File } from '@phosphor-icons/react';
import { useData } from '../../hooks/useData';
import { useApp } from '../../contexts/AppContext';
import { TABLES, MODAL_TYPES } from '../../constants';
import { formatBaseFloorName } from '../HomePage/OverviewPage/SpacesStructure/SpacesStructure';
import PageToolbar from '../shared/PageToolbar';
import pageStyles from '../../styles/pages.module.css';
import styles from './FilesPage.module.css';

const STORAGE_BUCKET = 'floor-plans';

export default function FilesPage() {
  const supabase = useSupabaseClient();
  const { currentProject, openModal } = useApp();
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [floorPlanUrls, setFloorPlanUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState({});

  // Fetch floors with floor plan data
  const {
    data: floors,
    isLoading: floorsLoading,
    error: floorsError,
  } = useData(TABLES.PROJECT_FLOORS, {
    enabled: Boolean(currentProject?.id),
    realtime: true,
    select: '*',
    filter: { project_id: currentProject?.id },
  });

  // Fetch floor plan analyses
  const floorIds = useMemo(() => floors?.map(f => f.id) || [], [floors]);

  const { data: analyses, isLoading: analysesLoading } = useData('floor_plan_analyses', {
    enabled: floorIds.length > 0,
    realtime: true,
    select: '*',
    filter: { floor_id: floorIds },
  });

  // Sort floors by floor_number
  const sortedFloors = useMemo(() => {
    if (!floors) return [];
    return [...floors].sort((a, b) => a.floor_number - b.floor_number);
  }, [floors]);

  // Set default selected floor
  useEffect(() => {
    if (!selectedFloor && sortedFloors.length > 0) {
      setSelectedFloor(sortedFloors[0].id);
    }
  }, [sortedFloors, selectedFloor]);

  // Get signed URL for a floor plan
  const getSignedUrl = useCallback(
    async (filePath, floorId) => {
      if (!filePath) return;

      setLoadingUrls(prev => ({ ...prev, [floorId]: true }));

      try {
        const { data, error } = await supabase.storage
          .from(STORAGE_BUCKET)
          .createSignedUrl(filePath, 60 * 60); // 1 hour

        if (error) {
          console.error('Error getting signed URL:', error, 'for path:', filePath);
          setFloorPlanUrls(prev => ({ ...prev, [floorId]: 'error' }));
        } else if (data?.signedUrl) {
          setFloorPlanUrls(prev => ({ ...prev, [floorId]: data.signedUrl }));
        }
      } catch (err) {
        console.error('Error getting signed URL:', err);
        setFloorPlanUrls(prev => ({ ...prev, [floorId]: 'error' }));
      } finally {
        setLoadingUrls(prev => ({ ...prev, [floorId]: false }));
      }
    },
    [supabase],
  );

  // Load URLs for floors with plans
  useEffect(() => {
    sortedFloors.forEach(floor => {
      if (floor.floor_plan_path) {
        // Only fetch if we don't already have a URL (or error) for this floor
        setFloorPlanUrls(prev => {
          if (prev[floor.id] === undefined) {
            getSignedUrl(floor.floor_plan_path, floor.id);
          }
          return prev;
        });
      }
    });
  }, [sortedFloors, getSignedUrl]);

  // Get analysis for a floor
  const getFloorAnalysis = useCallback(
    floorId => {
      if (!analyses) return null;
      return analyses.find(a => a.floor_id === floorId);
    },
    [analyses],
  );

  // Handle upload floor plan (new upload)
  const handleUploadFloorPlan = useCallback(
    floorId => {
      const floor = sortedFloors.find(f => f.id === floorId);
      openModal(MODAL_TYPES.FLOOR_PLAN_ANALYSIS, {
        floorId,
        floorName: floor ? formatBaseFloorName(floor.floor_number) : '',
        projectId: currentProject?.id,
      });
    },
    [openModal, currentProject?.id, sortedFloors],
  );

  // Handle re-analyze existing floor plan
  const handleReanalyzeFloorPlan = useCallback(
    floorId => {
      const floor = sortedFloors.find(f => f.id === floorId);
      if (!floor?.floor_plan_path) return;

      openModal(MODAL_TYPES.FLOOR_PLAN_ANALYSIS, {
        floorId,
        floorName: floor ? formatBaseFloorName(floor.floor_number) : '',
        projectId: currentProject?.id,
        existingFilePath: floor.floor_plan_path,
        existingFileUrl: floorPlanUrls[floorId],
      });
    },
    [openModal, currentProject?.id, sortedFloors, floorPlanUrls],
  );

  // Floor dropdown options
  const floorOptions = useMemo(() => {
    return sortedFloors.map(floor => ({
      id: floor.id,
      name: formatBaseFloorName(floor.floor_number),
    }));
  }, [sortedFloors]);

  // Get current floor data
  const currentFloor = useMemo(() => {
    return sortedFloors.find(f => f.id === selectedFloor);
  }, [sortedFloors, selectedFloor]);

  // Stats
  const stats = useMemo(() => {
    const totalFloors = sortedFloors.length;
    const floorsWithPlans = sortedFloors.filter(f => f.floor_plan_path).length;
    const analyzedFloors = analyses?.filter(a => a.status === 'completed').length || 0;

    return { totalFloors, floorsWithPlans, analyzedFloors };
  }, [sortedFloors, analyses]);

  if (floorsError) {
    return <div className={pageStyles.emptyState}>שגיאה בטעינת הנתונים</div>;
  }

  if (floorsLoading || analysesLoading) {
    return <div className={pageStyles.loadingContainer}>טוען...</div>;
  }

  if (sortedFloors.length === 0) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.emptyState}>
          <File size={48} className={pageStyles.emptyIcon} />
          <p className={pageStyles.emptyTitle}>אין קומות בפרויקט</p>
          <p className={pageStyles.emptyDescription}>
            הוסף קומות בהגדרות הפרויקט כדי להעלות תוכניות אדריכליות
          </p>
        </div>
      </div>
    );
  }

  // Primary selectors
  const primarySelectors = (
    <div className={pageStyles.dropdown}>
      <label className={pageStyles.dropdownLabel}>בחירת קומה</label>
      <select
        className={pageStyles.dropdownSelect}
        value={selectedFloor || ''}
        onChange={e => setSelectedFloor(e.target.value)}
      >
        {floorOptions.map(option => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );

  // Actions
  const actions = (
    <button
      onClick={() => handleUploadFloorPlan(selectedFloor)}
      className={pageStyles.primaryButton}
    >
      <UploadSimple size={18} />
      העלה תוכנית
    </button>
  );

  return (
    <div className={pageStyles.pageContainer}>
      <PageToolbar primarySelectors={primarySelectors} actions={actions} />

      {/* Summary Cards */}
      <div className={pageStyles.summarySection}>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>סה״כ קומות</p>
          <p className={pageStyles.summaryCardValue}>{stats.totalFloors}</p>
        </div>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>תוכניות שהועלו</p>
          <p className={pageStyles.summaryCardValue}>{stats.floorsWithPlans}</p>
        </div>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>ניתוחים שהושלמו</p>
          <p className={pageStyles.summaryCardValue}>{stats.analyzedFloors}</p>
        </div>
      </div>

      {/* Floor Plan Display */}
      {currentFloor && (
        <div className={styles.floorPlanSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              תוכנית {formatBaseFloorName(currentFloor.floor_number)}
            </h3>
            {currentFloor.floor_plan_uploaded_at && (
              <span className={styles.uploadDate}>
                הועלה: {new Date(currentFloor.floor_plan_uploaded_at).toLocaleDateString('he-IL')}
              </span>
            )}
          </div>

          {currentFloor.floor_plan_path ? (
            <div className={styles.floorPlanContent}>
              {loadingUrls[currentFloor.id] ? (
                <div className={styles.loadingImage}>טוען תמונה...</div>
              ) : floorPlanUrls[currentFloor.id] && floorPlanUrls[currentFloor.id] !== 'error' ? (
                <div className={styles.imageContainer}>
                  <img
                    src={floorPlanUrls[currentFloor.id]}
                    alt={`תוכנית ${formatBaseFloorName(currentFloor.floor_number)}`}
                    className={styles.floorPlanImage}
                    onError={e => {
                      console.error('Image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className={styles.imageActions}>
                    <a
                      href={floorPlanUrls[currentFloor.id]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.imageAction}
                    >
                      <Eye size={18} />
                      פתח בחלון חדש
                    </a>
                    <button
                      onClick={() => handleReanalyzeFloorPlan(currentFloor.id)}
                      className={styles.imageAction}
                    >
                      <ArrowsClockwise size={18} />
                      נתח מחדש
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.loadingImage}>
                  שגיאה בטעינת התמונה
                  <br />
                  <small style={{ color: '#94a3b8' }}>נתיב: {currentFloor.floor_plan_path}</small>
                </div>
              )}

              {/* Analysis Info */}
              {(() => {
                const analysis = getFloorAnalysis(currentFloor.id);
                if (!analysis) return null;

                return (
                  <div className={styles.analysisInfo}>
                    <h4 className={styles.analysisTitle}>פרטי ניתוח</h4>
                    <div className={styles.analysisDetails}>
                      <div className={styles.analysisItem}>
                        <span className={styles.analysisLabel}>סטטוס:</span>
                        <span className={`${styles.analysisValue} ${styles[analysis.status]}`}>
                          {analysis.status === 'completed'
                            ? 'הושלם'
                            : analysis.status === 'processing'
                              ? 'בעיבוד'
                              : analysis.status === 'failed'
                                ? 'נכשל'
                                : analysis.status}
                        </span>
                      </div>
                      {analysis.detected_spaces_count !== null && (
                        <div className={styles.analysisItem}>
                          <span className={styles.analysisLabel}>חללים שזוהו:</span>
                          <span className={styles.analysisValue}>
                            {analysis.detected_spaces_count}
                          </span>
                        </div>
                      )}
                      {analysis.created_at && (
                        <div className={styles.analysisItem}>
                          <span className={styles.analysisLabel}>תאריך ניתוח:</span>
                          <span className={styles.analysisValue}>
                            {new Date(analysis.created_at).toLocaleDateString('he-IL')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className={styles.emptyFloorPlan}>
              <Image size={64} className={styles.emptyIcon} />
              <p className={styles.emptyText}>לא הועלתה תוכנית לקומה זו</p>
              <button
                onClick={() => handleUploadFloorPlan(currentFloor.id)}
                className={pageStyles.primaryButton}
              >
                <UploadSimple size={18} />
                העלה תוכנית
              </button>
            </div>
          )}
        </div>
      )}

      {/* All Floors Overview */}
      <div className={styles.allFloorsSection}>
        <h3 className={styles.sectionTitle}>סקירת כל הקומות</h3>
        <div className={styles.floorsGrid}>
          {sortedFloors.map(floor => {
            const analysis = getFloorAnalysis(floor.id);
            const hasFloorPlan = Boolean(floor.floor_plan_path);

            return (
              <div
                key={floor.id}
                className={`${styles.floorCard} ${selectedFloor === floor.id ? styles.selected : ''}`}
                onClick={() => setSelectedFloor(floor.id)}
              >
                <div className={styles.floorCardHeader}>
                  <span className={styles.floorName}>
                    {formatBaseFloorName(floor.floor_number)}
                  </span>
                  {hasFloorPlan && (
                    <span className={`${styles.statusBadge} ${styles.uploaded}`}>הועלה</span>
                  )}
                </div>
                <div className={styles.floorCardContent}>
                  {hasFloorPlan ? (
                    <>
                      {floorPlanUrls[floor.id] && floorPlanUrls[floor.id] !== 'error' ? (
                        <img
                          src={floorPlanUrls[floor.id]}
                          alt={formatBaseFloorName(floor.floor_number)}
                          className={styles.thumbnailImage}
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className={styles.thumbnailPlaceholder}>
                          <Image size={32} />
                        </div>
                      )}
                      {analysis && (
                        <div className={styles.floorCardMeta}>
                          <span className={`${styles.analysisBadge} ${styles[analysis.status]}`}>
                            {analysis.status === 'completed'
                              ? `${analysis.detected_spaces_count || 0} חללים`
                              : analysis.status === 'processing'
                                ? 'מעבד...'
                                : 'נכשל'}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.noFloorPlan}>
                      <File size={32} />
                      <span>אין תוכנית</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
